import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FORMATS, FORMATS_EN } from "@/lib/data";
import { STRINGS } from "@/lib/i18n";
import { useLanguage } from "@/context/LanguageContext";

const spans = ["md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-4"];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Formats({ onSelect }) {
  const { lang } = useLanguage();
  const t = STRINGS[lang].formatsSection;
  const formats = lang === "en" ? FORMATS_EN : FORMATS;

  return (
    <section id="formatos" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 lg:py-36" data-testid="formats-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="mb-16 max-w-2xl"
      >
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{t.overline}</p>
        <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
          {t.title[0]}
          <em className="italic text-blue-400">{t.title[1]}</em>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-slate-300/90">{t.copy}</p>
      </motion.div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-6">
        {formats.map((format, i) => (
          <motion.article
            key={format.id}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={cardVariants}
            className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B101C] transition-shadow duration-500 hover:shadow-[0_0_40px_rgba(59,130,246,0.18)] ${spans[i]}`}
            data-testid={`format-card-${format.id}`}
          >
            <div className="relative h-64 overflow-hidden sm:h-72">
              <img
                src={format.image}
                alt={format.name}
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070D] via-[#05070D]/40 to-transparent" aria-hidden="true" />
              <span className="absolute left-5 top-5 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-400/90">
                {format.index} — {format.tag}
              </span>
            </div>
            <div className="relative -mt-14 p-6">
              <h3 className="font-serif text-2xl text-blue-50">{format.name}</h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">{format.desc}</p>
              <button
                onClick={() => onSelect(format.id)}
                data-testid={`format-book-${format.id}`}
                className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-blue-400 transition-colors duration-300 hover:text-blue-300"
              >
                {t.bookCta}
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
